import { useState, useMemo, lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@shared/supabase/client';
import { useSupabaseQuery, useInvalidateSupabase } from '@shared/supabase/hooks';
import { Skeleton, Button, VerifiedBadge } from '@shared/ui';
import { IconSparkle } from '@shared/ui/icons';
import { useHandleVerification } from '@shared/hooks/useHandleVerification';
import { IgLink } from '@shared/ui/IgLink';
import { CampaignChat } from '@shared/ui/CampaignChat';
import { UnreadBadge } from '@shared/ui/UnreadBadge';
import { useUnreadThreads } from '@shared/hooks/useUnreadThreads';
import { captureError } from '@shared/lib/observability';

const CreatorPreviewModal = lazy(() => import('./CreatorPreviewModal'));

// Applicant-review + auto comparison + overlap-aware lineup recommender for one
// campaign. Per-applicant analysis reuses the price-check model; the recommender
// picks the best K creators as a GROUP (value/reach/fit, within budget, avoiding
// audience overlap via get_shortlist_overlap), with a category-experience lever.

interface Applicant {
  application_id: string;
  status: 'applied' | 'shortlisted' | 'accepted' | 'rejected' | 'withdrawn';
  pitch_note: string | null;
  proposed_price: number | null;
  currency: string;
  provider_id: string;
  handle: string;
  followers: number | null;
  account_er: number | null;
  median_reel_views: number | null;
  distinct_brands: number | null;
  suggested_price: number | null;
  value_ratio: number | null;
  niche_match: number;
  category_brands: number;
  category_brand_names: string[] | null;
  works_with_campaign_brand: boolean;
  country: string[] | null;
  content_languages: string[] | null;
  pct_branded: number | null;
}
interface OverlapPair { a_provider_id: string; b_provider_id: string; band: 'high' | 'medium' | 'low' }

type Mode = 'balanced' | 'reach' | 'value';
type Exp = 'any' | 'experienced' | 'fresh';

const fmtK = (n: number | null | undefined) =>
  n == null ? '—' : n >= 1_000_000 ? `${(n / 1e6).toFixed(1)}M` : n >= 1000 ? `${Math.round(n / 1000)}K` : String(n);
const money = (n: number | null, cur = 'KGS') => (n == null ? null : `${n.toLocaleString('ru-RU')} ${cur === 'KGS' ? 'сом' : cur}`);
const reachOf = (a: Applicant) => a.median_reel_views ?? (a.followers ? Math.round(a.followers * 0.3) : 0);
const isLocal = (a: Applicant) => (a.country ?? []).includes('KG');

const COUNTRY: Record<string, { flag: string; name: string }> = {
  KG: { flag: '🇰🇬', name: 'KG' }, KZ: { flag: '🇰🇿', name: 'KZ' }, RU: { flag: '🇷🇺', name: 'RU' },
  UZ: { flag: '🇺🇿', name: 'UZ' }, TJ: { flag: '🇹🇯', name: 'TJ' }, TR: { flag: '🇹🇷', name: 'TR' }, US: { flag: '🇺🇸', name: 'US' },
};
function verdict(r: number | null, tr: (k: string, d: string) => string): { label: string; color: string } {
  if (r == null || !isFinite(r)) return { label: tr('applicants.v.na', 'Без цены'), color: '#9aa' };
  if (r < 0.6) return { label: tr('applicants.v.excellent', 'Отличная цена'), color: '#37d67a' };
  if (r < 0.95) return { label: tr('applicants.v.good', 'Хорошая'), color: '#8bd450' };
  if (r < 1.35) return { label: tr('applicants.v.fair', 'Справедливая'), color: '#e6c84a' };
  if (r < 2.0) return { label: tr('applicants.v.pricey', 'Дороговато'), color: '#e8965a' };
  return { label: tr('applicants.v.overpriced', 'Завышена'), color: '#e8607a' };
}

// Greedy overlap-aware group selection. Explainable: sort by mode score, add best
// candidate that doesn't HIGH-overlap an already-picked one and fits the budget.
function recommendSet(
  pool: Applicant[], overlap: OverlapPair[],
  opts: { mode: Mode; exp: Exp; slots: number; budget: number | null; basis: string; barter: boolean },
): { ids: string[]; total: number; reach: number } {
  const maxReach = Math.max(1, ...pool.map(reachOf));
  const score = (a: Applicant) => {
    const reachN = reachOf(a) / maxReach;
    const valueN = a.value_ratio ? Math.min(2, 1 / a.value_ratio) / 2 : 0.4;
    const nicheN = a.niche_match > 0 ? 1 : 0;
    let s = opts.mode === 'reach' ? reachN : opts.mode === 'value' ? valueN : 0.45 * reachN + 0.35 * valueN + 0.2 * nicheN;
    const competitorExp = (a.category_brands ?? 0) - (a.works_with_campaign_brand ? 1 : 0);
    if (opts.exp === 'experienced' && (a.category_brands ?? 0) > 0) s += 0.15;
    if (opts.exp === 'fresh' && competitorExp > 0) s -= 0.25;
    if (opts.mode === 'balanced' && !isLocal(a)) s -= 0.2; // local (KG) preferred in balanced
    return s;
  };
  const hi = new Map<string, Set<string>>();
  overlap.filter((o) => o.band === 'high').forEach((o) => {
    if (!hi.has(o.a_provider_id)) hi.set(o.a_provider_id, new Set());
    if (!hi.has(o.b_provider_id)) hi.set(o.b_provider_id, new Set());
    hi.get(o.a_provider_id)!.add(o.b_provider_id);
    hi.get(o.b_provider_id)!.add(o.a_provider_id);
  });
  const sorted = [...pool].sort((a, b) => score(b) - score(a));
  const picked: Applicant[] = [];
  let total = 0;
  for (const a of sorted) {
    if (picked.length >= opts.slots) break;
    const conflicts = hi.get(a.provider_id);
    if (conflicts && picked.some((p) => conflicts.has(p.provider_id))) continue;
    const price = a.proposed_price ?? 0;
    if (!opts.barter && opts.budget != null) {
      if (opts.basis === 'per_creator' && price > opts.budget) continue;
      if (opts.basis === 'total' && total + price > opts.budget) continue;
    }
    picked.push(a); total += price;
  }
  return { ids: picked.map((p) => p.application_id), total, reach: picked.reduce((s, p) => s + reachOf(p), 0) };
}

export default function CampaignApplicants({
  campaignId, title, maxCreators, budget, budgetBasis, compensationType, onClose, onChanged,
}: {
  campaignId: string; title: string; maxCreators: number;
  budget: number | null; budgetBasis: string; compensationType: string;
  onClose: () => void; onChanged: () => void;
}) {
  const { t } = useTranslation('customer');
  const tr = (k: string, d: string, o?: Record<string, unknown>) => t(k, { defaultValue: d, ...o });
  const invalidate = useInvalidateSupabase();
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>('balanced');
  const [exp, setExp] = useState<Exp>('any');
  const [rec, setRec] = useState<{ ids: string[]; total: number; reach: number } | null>(null);
  const [card, setCard] = useState<{ id: string; handle: string; followers: number; er: number } | null>(null);
  const [chatApp, setChatApp] = useState<Applicant | null>(null);
  const { byApplication } = useUnreadThreads();

  const { data, isLoading, refetch } = useSupabaseQuery<Applicant[]>(
    ['campaign-applicants', campaignId],
    async () => supabase.rpc('get_campaign_applicants', { p_campaign_id: campaignId }),
  );
  const rows = useMemo(() => data ?? [], [data]);
  const providerIds = useMemo(() => rows.map((r) => r.provider_id), [rows]);
  const verif = useHandleVerification(rows.map((r) => r.handle));

  const { data: overlap } = useSupabaseQuery<OverlapPair[]>(
    ['campaign-overlap', providerIds.join(',')],
    async () => supabase.rpc('get_shortlist_overlap', { p_provider_ids: providerIds }),
    { enabled: providerIds.length > 1 },
  );

  // Which applicants self-declared they accept barter — shown as a chip so a
  // business can spot barter-friendly creators (useful for barter campaigns).
  const { data: barterRows } = useSupabaseQuery<{ id: string; accepts_barter: boolean }[]>(
    ['campaign-applicant-barter', providerIds.join(',')],
    async () => supabase.from('instagram_providers').select('id, accepts_barter').in('id', providerIds),
    { enabled: providerIds.length > 0 },
  );
  const barterSet = useMemo(() => new Set((barterRows ?? []).filter((b) => b.accepts_barter).map((b) => b.id)), [barterRows]);

  const acceptedCount = rows.filter((r) => r.status === 'accepted').length;
  const pool = rows.filter((r) => r.status === 'applied' || r.status === 'shortlisted');
  const slots = Math.max(0, maxCreators - acceptedCount);
  const recIds = new Set(rec?.ids ?? []);

  const runRecommend = () => {
    const r = recommendSet(pool, overlap ?? [], {
      mode, exp, slots, budget, basis: budgetBasis,
      barter: compensationType === 'barter',
    });
    setRec(r);
  };

  const review = async (id: string, status: 'shortlisted' | 'accepted' | 'rejected') => {
    setBusy(id); setErr(null);
    const { error } = await supabase.rpc('review_application', { p_application_id: id, p_status: status });
    setBusy(null);
    if (error) {
      captureError(error, { where: 'applicants.review', id, status });
      setErr(error.message.includes('max creators') ? tr('applicants.maxReached', 'Достигнут лимит выбранных блогеров.') : error.message);
      return;
    }
    invalidate(['campaign-applicants']); invalidate(['my-campaigns']); refetch(); onChanged();
  };

  const applySet = async () => {
    if (!rec?.ids.length) return;
    setBusy('set'); setErr(null);
    for (const id of rec.ids) {
      const { error } = await supabase.rpc('review_application', { p_application_id: id, p_status: 'accepted' });
      if (error) {
        captureError(error, { where: 'applicants.applySet', status });
        setErr(error.message.includes('max creators') ? tr('applicants.maxReached', 'Достигнут лимит.') : error.message);
        break;
      }
    }
    setBusy(null); setRec(null);
    invalidate(['campaign-applicants']); invalidate(['my-campaigns']); refetch(); onChanged();
  };

  const seg = (active: boolean): React.CSSProperties => ({
    padding: '5px 11px', borderRadius: 14, cursor: 'pointer', fontFamily: 'inherit', fontSize: 12,
    border: `1px solid ${active ? '#4a9eff' : 'rgba(255,255,255,0.14)'}`,
    background: active ? 'rgba(74,158,255,0.16)' : 'transparent', color: active ? '#bcd0ff' : '#9aa',
  });

  return (
    <div onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 50, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', overflowY: 'auto', padding: '30px 16px' }}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: '#0d1017', border: '1px solid rgba(15,45,100,0.5)', borderRadius: 14, padding: 22, width: '100%', maxWidth: 780 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10, marginBottom: 4 }}>
          <div>
            <h2 className="c-page-title" style={{ margin: 0 }}>{tr('applicants.title', 'Отклики')}: {title}</h2>
            <div style={{ color: 'var(--muted)', fontSize: 13, marginTop: 4 }}>
              {tr('applicants.count', 'Откликнулось {{n}} · нужно {{k}} · принято {{a}}', { n: rows.filter((r) => r.status !== 'rejected').length, k: maxCreators, a: acceptedCount })}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'var(--dim)', fontSize: 22, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        {/* recommender */}
        {slots > 0 && pool.length > 1 && (
          <div style={{ background: 'rgba(74,158,255,0.06)', border: '1px solid rgba(74,158,255,0.22)', borderRadius: 10, padding: '12px 14px', margin: '12px 0 14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'var(--text)', fontSize: 13.5, fontWeight: 600, marginBottom: 8 }}><IconSparkle size={15} color="#6699ff" />{tr('applicants.recTitle', 'Подобрать оптимальный набор')} ({slots})</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
              {([['balanced', tr('applicants.mode.balanced', 'Баланс')], ['reach', tr('applicants.mode.reach', 'Макс. охват')], ['value', tr('applicants.mode.value', 'Лучшая цена')]] as const).map(([m, l]) => (
                <button key={m} onClick={() => { setMode(m); setRec(null); }} style={seg(mode === m)}>{l}</button>
              ))}
              <span style={{ width: 1, background: 'rgba(255,255,255,0.12)', margin: '0 2px' }} />
              {([['any', tr('applicants.exp.any', 'Опыт: любой')], ['experienced', tr('applicants.exp.exp', 'Опытные в категории')], ['fresh', tr('applicants.exp.fresh', 'Без конкурентов')]] as const).map(([e, l]) => (
                <button key={e} onClick={() => { setExp(e); setRec(null); }} style={seg(exp === e)}>{l}</button>
              ))}
            </div>
            {!rec ? (
              <Button variant="primary" onClick={runRecommend} style={{ minHeight: 34, padding: '5px 16px' }}>{tr('applicants.recRun', 'Подобрать')}</Button>
            ) : rec.ids.length === 0 ? (
              <div style={{ color: '#e8965a', fontSize: 12.5 }}>{tr('applicants.recNone', 'Не удалось подобрать набор в рамках бюджета/без пересечений.')}</div>
            ) : (
              <div>
                <div style={{ color: 'var(--accent-text)', fontSize: 12.5, marginBottom: 8 }}>
                  {tr('applicants.recSummary', 'Рекомендуем {{n}}: суммарный охват ~{{r}}{{price}}, без высокого пересечения аудиторий.', {
                    n: rec.ids.length, r: fmtK(rec.reach),
                    price: compensationType === 'barter' ? '' : `, бюджет ${money(rec.total)}`,
                  })}
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <Button variant="primary" onClick={() => applySet()} loading={busy === 'set'} style={{ minHeight: 34, padding: '5px 16px' }}>{tr('applicants.recAccept', 'Принять набор')}</Button>
                  <Button variant="ghost" onClick={() => setRec(null)} style={{ minHeight: 34, padding: '5px 12px' }}>{tr('applicants.recReset', 'Сбросить')}</Button>
                </div>
              </div>
            )}
          </div>
        )}

        <p style={{ color: 'var(--dim)', fontSize: 12, margin: '0 0 14px' }}>
          {tr('applicants.hint', 'Цена сравнивается со «справедливой» по нашей модели (как во «Проверке цен»). Сверху — лучшие по цене/охвату.')}
        </p>

        {err && <div style={{ color: '#e74c3c', fontSize: 13, marginBottom: 10 }}>{err}</div>}
        {isLoading && <><Skeleton height={110} style={{ marginBottom: 10 }} /><Skeleton height={110} /></>}
        {!isLoading && rows.length === 0 && (
          <div className="empty-state"><p style={{ margin: 0 }}>{tr('applicants.empty', 'Пока никто не откликнулся.')}</p></div>
        )}

        <div style={{ display: 'grid', gap: 10 }}>
          {rows.map((a) => {
            const v = verdict(a.value_ratio, tr);
            const isRec = recIds.has(a.application_id);
            const competitors = (a.category_brands ?? 0) - (a.works_with_campaign_brand ? 1 : 0);
            const stats = [
              `${fmtK(a.followers)} ${tr('applicants.followers', 'подписчиков')}`,
              `ER ${a.account_er != null ? Number(a.account_er).toFixed(1) : '—'}%`,
              `${fmtK(a.median_reel_views)}/${tr('applicants.reel', 'рилс')}`,
              `${a.distinct_brands ?? 0} ${t('applicants.brands', { count: a.distinct_brands ?? 0 })}`,
              (a.content_languages ?? []).length ? (a.content_languages ?? []).map((l) => l.toUpperCase()).join('/') : null,
            ].filter(Boolean).join('   ·   ');
            const flags: { key: string; text: string; color: string }[] = [];
            if (a.niche_match > 0) flags.push({ key: 'n', text: tr('applicants.nicheFit', 'в вашей нише'), color: '#8bd450' });
            if (a.works_with_campaign_brand) flags.push({ key: 'w', text: tr('applicants.worksWithYou', 'работал с вашим брендом'), color: '#37d67a' });
            else if (competitors > 0) flags.push({ key: 'c', text: `${tr('applicants.catExp', 'в категории')}: ${(a.category_brand_names ?? []).filter(Boolean).slice(0, 3).join(', ')}`, color: '#e6c84a' });
            if (a.pct_branded != null && a.pct_branded >= 0.4) flags.push({ key: 'a', text: `${tr('applicants.adHeavy', 'реклама')} ${Math.round(a.pct_branded * 100)}%`, color: '#e8965a' });
            return (
              <div key={a.application_id} style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(15,45,100,0.4)', borderLeft: `3px solid ${isRec ? 'var(--accent)' : a.status === 'accepted' ? '#37d67a' : 'rgba(15,45,100,0.4)'}`, borderRadius: 10, padding: '13px 15px', opacity: a.status === 'rejected' ? 0.5 : 1 }}>
                {/* identity + verdict */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', minWidth: 0 }}>
                    {isRec && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: '#6699ff', fontSize: 11, fontWeight: 600 }}><IconSparkle size={12} color="#6699ff" />{tr('applicants.recBadge', 'рекомендуем')}</span>}
                    <button onClick={() => setCard({ id: a.provider_id, handle: a.handle, followers: a.followers ?? 0, er: a.account_er ?? 0 })} title={tr('applicants.openCard', 'Открыть карточку')} style={{ background: 'transparent', border: 'none', padding: 0, color: 'var(--text)', fontWeight: 600, fontSize: 15, cursor: 'pointer', fontFamily: 'inherit' }}>@{a.handle}</button>
                    <VerifiedBadge {...verif(a.handle)} size={15} />
                    <IgLink handle={a.handle} size={16} />
                    {barterSet.has(a.provider_id) && <span title={tr('applicants.barterHint', 'Готов(а) к бартеру')} style={{ fontSize: 11, fontWeight: 600, color: '#e6c84a', background: 'rgba(230,200,74,0.14)', border: '1px solid rgba(230,200,74,0.4)', borderRadius: 12, padding: '2px 8px' }}>{tr('applicants.barter', 'бартер')}</span>}
                    {(a.country ?? []).map((cc) => { const info = COUNTRY[cc]; const foreign = cc !== 'KG'; return <span key={cc} title={foreign ? tr('applicants.foreign', 'аудитория не в Кыргызстане') : ''} style={{ fontSize: 12, color: foreign ? '#e6c84a' : 'var(--muted)' }}>{info ? `${info.flag} ${info.name}` : cc}</span>; })}
                  </div>
                  <span style={{ flexShrink: 0, fontSize: 12, fontWeight: 600, color: v.color, background: `${v.color}1e`, border: `1px solid ${v.color}55`, borderRadius: 16, padding: '3px 10px' }}>{v.label}</span>
                </div>
                {/* price — the decision */}
                <div style={{ marginTop: 9, fontSize: 14.5 }}>
                  <b style={{ color: '#fff' }}>{money(a.proposed_price, a.currency) ?? tr('applicants.noPrice', 'Цена не указана')}</b>
                  {a.suggested_price != null && <span style={{ color: '#6b7688', marginLeft: 8, fontSize: 13 }}>{tr('applicants.fair', 'справедливо')} ~{money(a.suggested_price, a.currency)}</span>}
                </div>
                {/* stats */}
                <div style={{ marginTop: 6, color: 'var(--muted)', fontSize: 12.5 }}>{stats}</div>
                {/* sparse signal flags */}
                {flags.length > 0 && (
                  <div style={{ marginTop: 7, display: 'flex', flexWrap: 'wrap', gap: 14, fontSize: 12 }}>
                    {flags.map((f) => <span key={f.key} style={{ color: f.color }}>{f.text}</span>)}
                  </div>
                )}
                {a.pitch_note && <div style={{ color: '#9aa6b6', fontSize: 12.5, fontStyle: 'italic', marginTop: 9, borderLeft: '2px solid rgba(255,255,255,0.1)', paddingLeft: 9 }}>“{a.pitch_note}”</div>}
                <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  {a.status === 'accepted' ? (
                    <div style={{ color: '#37d67a', fontSize: 12.5, display: 'inline-flex', alignItems: 'center', gap: 6 }}>{tr('applicants.acceptedContact', 'Принят — напишите в Instagram')}: <IgLink handle={a.handle} size={16} /></div>
                  ) : a.status === 'rejected' ? (
                    <button onClick={() => review(a.application_id, 'shortlisted')} style={{ background: 'transparent', border: 'none', color: '#6699ff', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', padding: 0 }}>{tr('applicants.undo', 'вернуть в рассмотрение')}</button>
                  ) : (
                    <div style={{ display: 'flex', gap: 8 }}>
                      <Button variant="primary" onClick={() => review(a.application_id, 'accepted')} loading={busy === a.application_id} style={{ minHeight: 34, padding: '4px 20px' }}>{tr('applicants.accept', 'Принять')}</Button>
                      <Button variant="ghost" onClick={() => review(a.application_id, 'rejected')} loading={busy === a.application_id} style={{ minHeight: 34, padding: '4px 14px', color: '#e8607a' }}>{tr('applicants.reject', 'Отклонить')}</Button>
                    </div>
                  )}
                  <button onClick={() => setChatApp(a)} title={tr('applicants.chat', 'Чат')}
                    style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(74,158,255,0.14)', border: '1px solid rgba(74,158,255,0.4)', color: 'var(--accent-text)', borderRadius: 8, padding: '5px 12px', fontSize: 12.5, cursor: 'pointer', fontFamily: 'inherit' }}>
                    {tr('applicants.chat', 'Чат')}
                    <UnreadBadge count={byApplication.get(a.application_id) ?? 0} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {chatApp && (
        <CampaignChat
          applicationId={chatApp.application_id}
          title={`@${chatApp.handle}`}
          subtitle={title}
          onClose={() => setChatApp(null)}
        />
      )}

      {card && (
        <Suspense fallback={null}>
          <CreatorPreviewModal handle={card.handle} instagramProviderId={card.id} followers={card.followers} engagement={card.er} onClose={() => setCard(null)} />
        </Suspense>
      )}
    </div>
  );
}
